/*       */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Box } from '@mic3/platform-ui';

import Loader from 'app/components/atoms/Loader/Loader';

import { addWorkspaceUsers, removeWorkspaceUsers } from 'store/actions/admin/workspacesActions';
import { clearUserResourcesList, loadUserAboutWorkspaces, loadUserWorkspaces } from 'store/actions/admin/userManagementAction';

import { bind } from 'app/utils/decorators/decoratorUtils';
import { getPermissions } from 'app/config/rolesConfig';
import Immutable from 'app/utils/immutable/Immutable';
import { isEmpty } from 'app/utils/utils';
import LazySelectionList from 'app/containers/Common/SelectionList/LazySelectionList';


const SelectionListWrapper = styled(Box)`
    .MuiExpansionPanelSummary-root.Mui-expanded {
        min-height: 0 !important;
    }

    .MuiExpansionPanelDetails-root {
        margin-top: 0 !important;    
        padding-bottom:0 !important;
    }
    .button-wrapper{
        margin-right: 10px;
    }
    height: 100%;
`;

class UserWorkspaces extends PureComponent {
    static propTypes = {
        user: PropTypes.object, // user details
        isLoading: PropTypes.bool,
        loadUserWorkspaces: PropTypes.func,
        addWorkspaceUsers: PropTypes.func,
        removeWorkspaceUsers: PropTypes.func,
    };
    listRef = React.createRef();
    defaultStartStopIndex = { startIndex: 0, stopIndex: 30 };

    constructor(props) {
        super(props);
        this.state = Immutable({
            listKey: 0,
            isUpdatingUserWorkspaces: false,
        });
        this.defaultState = this.state;
    }

    componentWillUnmount() {
        this.props.clearUserResourcesList();
    }

    componentDidUpdate(prevProps) {
        const { user: { id: prevId } = {} } = prevProps || {};
        const { user: { id: newId } = {} } = this.props || {};
        if (prevId !== newId) {
            this.loadUserWorkspaces();
        }
    }

    @bind
    refreshList() {
        this.listRef.current?.forceUpdate();
        this.setState({ isUpdatingUserWorkspaces: false, listKey: this.state.listKey + 1, });
    }

    @bind
    async loadUserWorkspaces(options) {
        const { user } = this.props;
        await this.props.loadUserWorkspaces(user?.id, options);
    }

    @bind
    async loadUserAboutWorkspaces() {
        const { user } = this.props;
        await this.props.loadUserAboutWorkspaces(user?.id, this.defaultStartStopIndex);
    }

    @bind
    async onSubmitWorkspaces(newWorkspaces) {
        const { user: { id: userId } = {}, workspaces: existingWorkspaces, addWorkspaceUsers } = this.props || {};
        if (!userId || isEmpty(newWorkspaces)) {
            return;
        }
        this.setState({isUpdatingUserWorkspaces: true})
        const addedPromises = newWorkspaces.map(async workspace => {
            if (!existingWorkspaces.find(t => t.id === workspace.id)) {
                await addWorkspaceUsers(workspace.id, [{ userId, role: workspace.role }]);
            }
        }, []);
        await Promise.all(addedPromises).then(() => {
            this.loadUserWorkspaces();
            this.loadUserAboutWorkspaces();
            this.refreshList();
        });
    }

    @bind
    async removeWorkspaces(selectedWorkspaceListIds) {
        const { user: { id: userId } = {}, removeWorkspaceUsers } = this.props || {};
        if (!userId || isEmpty(selectedWorkspaceListIds)) {
            return;
        }
        this.setState({isUpdatingUserWorkspaces: true})
        const removePromises = selectedWorkspaceListIds.map(async workspaceId => {
            await removeWorkspaceUsers(workspaceId, [userId]);
        });
        await Promise.all(removePromises).then(() => {
            this.loadUserWorkspaces();
            this.loadUserAboutWorkspaces();
            this.refreshList();
        });
    }
    
    @bind
    async updateRole(role, data) {
        const {user, addWorkspaceUsers } = this.props;
        if (role === data.role || !data?.id || !user?.id) {
            return;
        }
        const { id: userId } = user;
        const payload = [{ role, userId }];
        await addWorkspaceUsers(data.id, payload);
        this.listRef?.current?.updateItemData(data.id, { role });
    }

    render() {
        const { user, workspaces, isLoading, totalRecords, startIndex } = this.props || {};
        const { canEdit } = getPermissions(user.role);
        const { listKey, isUpdatingUserWorkspaces } = this.state;
        const filterBy = [
            { field: 'isSystem', op: '=', value: false },
            {
                'field': 'teams.team.isSystem',
                'op': '=',
                'value': true
            }];
        const excludeBy = [
            { field: 'teams.team.users.user.id', op: '=', value: user?.id },
            {
                'field': 'teams.team.users.role',
                'op': 'is not null',
            }
        ];

        return (
            <>
                {isUpdatingUserWorkspaces && <Loader absolute backdrop />}
                <SelectionListWrapper>
                    <LazySelectionList
                        key={listKey}
                        list={workspaces}
                        ref={this.listRef}
                        type="workspace"
                        onAdd={this.onSubmitWorkspaces}
                        onRemove={this.removeWorkspaces}
                        isLoading={isLoading}
                        totalRecords={totalRecords}
                        startIndex={startIndex}
                        loadData={this.loadUserWorkspaces}
                        showRoles
                        userRole={user.role}
                        disabled={!canEdit }
                        setRole={this.updateRole}
                        filterBy={filterBy}
                        excludeBy={excludeBy}
                        fullHeight
                        showSearch
                    />
                </SelectionListWrapper>
            </>
        );
    }
}

export default connect(
    state => ({
        isLoading: state.admin.users.workspaces.isLoading,
        workspaces: state.admin.users.workspaces.records,
        totalRecords: state.admin.users.workspaces.count,
        startIndex: state.admin.users.workspaces.startIndex,
        loggedInUserProfile: state.user.profile,
    }),
    {
        loadUserWorkspaces,
        loadUserAboutWorkspaces,
        addWorkspaceUsers,
        removeWorkspaceUsers,
        clearUserResourcesList,
    }
)(UserWorkspaces);
